import { withLoading } from "@/api/api";
import { normalizarPagina } from "@/helpers/apuracaoCapex";
import { get, post, remover } from "./http";

/**
 * Service da tela `classificacao-de-fase.vue` (API real).
 * `GET`/`POST`/`DELETE v1/apuracaocapex/{empresaId}/classificacoes/{tipo}`, com `tipo` =
 * `timesheets`, `terceiros`, `materiais` ou `consumo` (na tela, "aba").
 *
 * @typedef {object} ItemClassificacao
 * @property {number} id - id do lançamento
 * @property {string} competencia - `AAAA-MM`
 * @property {number} projeto_id
 * @property {string} projeto
 * @property {string} fase - "Pesquisa" | "Desenvolvimento" | "" (não classificado)
 * @property {boolean} periodo_fechado - só itens de período fechado podem ser classificados
 * @property {number} valor - horas na aba Timesheets; R$ nas demais
 */

/** Abas (telas) suportadas — a ordem aqui é a ordem das abas na tela. */
export const ABAS = [
  { value: "timesheets", text: "Timesheets" },
  { value: "terceiros", text: "Serviços de Terceiros" },
  { value: "materiais", text: "Materiais" },
  { value: "consumo", text: "Consumo de Materiais" },
];

/** Unidade de medida da aba: Timesheets são **horas**; as demais, **dinheiro**. */
export const unidadeDaAba = (aba) => (aba === "timesheets" ? "horas" : "moeda");

/** Opções do filtro de fase (nos valores que a tela usa). */
export const FASES = [
  { text: "Todas", value: "" },
  { text: "Pesquisa", value: "Pesquisa" },
  { text: "Desenvolvimento", value: "Desenvolvimento" },
  { text: "Não classificado", value: "nao-classificado" },
];

/** Rótulo da fase (vazio ⇒ "Não classificado"). */
export const labelFase = (fase) => fase || "Não classificado";

/** Competência `AAAA-MM` (ou `AAAA-MM-DD`) → `MM/AAAA`, o formato exibido na tela. */
export const formatarCompetencia = (valor) => {
  if (valor === null || valor === undefined || valor === "") return "Indefinido";

  const partes = String(valor).trim().match(/^(\d{4})-(\d{2})(?:-\d{2})?$/);

  if (!partes) return "Inválido";

  const [, ano, mes] = partes;

  if (Number(mes) < 1 || Number(mes) > 12) return "Inválido";

  return `${mes}/${ano}`;
};

const LOADING_KEY = "apuracaocapex-classificacao";

/** Chave própria da **gravação** (classificar/limpar) no overlay global. */
const GRAVACAO_KEY = "apuracaocapex-classificacao-gravacao";

/** Fase da tela → valor de `filters[fase]` na API. */
const faseParaFiltro = (fase) => (fase === "nao-classificado" ? "nao_classificado" : String(fase || "").toLowerCase());

/** Fase da tela → valor de gravação da API (1 = Pesquisa, 2 = Desenvolvimento). */
const faseParaGravacao = (fase) => (fase === "Pesquisa" ? 1 : 2);

/** `MM/AAAA` (o formato que o filtro da tela usa) → `AAAA-MM`, o formato da API. */
const competenciaParaApi = (competencia) => {
  const partes = String(competencia || "").match(/^(\d{2})\/(\d{4})$/);

  return partes ? `${partes[2]}-${partes[1]}` : String(competencia || "");
};

/** Texto digitado no filtro, sem espaços nas pontas. */
const texto = (valor) => String(valor || "").trim();

/** Filtros no formato da API (só o que o usuário escolheu). */
const montarFiltros = (filtros = {}, rota) => {
  const competencia = competenciaParaApi(texto(filtros.competencia));
  const projeto = texto(filtros.projeto);
  const colaborador = texto(filtros.colaborador);
  const mapa = {
    competencia: competencia ? [competencia] : [],
    projeto: projeto ? [projeto] : [],
    fase: filtros.fase ? [faseParaFiltro(filtros.fase)] : [],
  };

  if (rota === "timesheets" && colaborador) mapa.colaborador = [colaborador];

  return mapa;
};

/**
 * Lista os lançamentos de uma aba, paginados/filtrados/ordenados.
 *
 * ⚠️ A API não aceita `per_page = -1`: com `perPage <= 0` pedimos 1 registro para descobrir
 * o `meta.total` e repetimos a busca com esse tamanho.
 *
 * @param {{ empresaId: number|string, anoBase: string|number, tipo?: string, aba?: string,
 *   page?: number, perPage?: number, sort?: object|Array, filtros?: object }} params
 * @returns {Promise<{ rows: ItemClassificacao[], total: number, page: number, perPage: number,
 *   totais: object }>}
 */
export const listarItens = async ({
  empresaId,
  anoBase,
  tipo = null,
  aba = "terceiros",
  page = 1,
  perPage = 25,
  sort = [],
  filtros = {},
}) => {
  const rota = tipo || aba;

  const buscar = async () => {
    const params = {
      ano_base: Number(anoBase) || undefined,
      page,
      per_page: perPage > 0 ? perPage : 1,
      filters: montarFiltros(filtros, rota),
      sort,
    };

    let corpo = await get(empresaId, `classificacoes/${rota}`, params);

    if (perPage <= 0) {
      const total = (corpo.meta && corpo.meta.total) || 0;

      if (total > 1) corpo = await get(empresaId, `classificacoes/${rota}`, { ...params, page: 1, per_page: total });
    }

    const pagina = normalizarPagina(corpo, { perPage: perPage > 0 ? perPage : 25 });

    return pagina;
  };

  return withLoading(LOADING_KEY, buscar);
};

/**
 * Classifica lançamentos como Pesquisa ou Desenvolvimento (`itens` é sempre lista).
 * @returns {Promise<Array>} registros gravados
 */
export const classificarItens = ({ empresaId, anoBase, tipo = null, aba, itens, fase }) =>
  withLoading(GRAVACAO_KEY, () =>
    post(empresaId, `classificacoes/${tipo || aba}`, {
      ano_base: Number(anoBase) || undefined,
      lancamentos: itens || [],
      fase: faseParaGravacao(fase),
    }).then((resposta) => (resposta && resposta.data) || [])
  );

/**
 * Limpa a classificação (volta para "Não classificado") — exige a permissão de exclusão.
 * @returns {Promise<{ removidas?: number }>}
 */
export const limparClassificacao = ({ empresaId, anoBase, tipo = null, aba, itens }) =>
  withLoading(GRAVACAO_KEY, () =>
    remover(empresaId, `classificacoes/${tipo || aba}`, {
      ano_base: Number(anoBase) || undefined,
      lancamentos: itens || [],
    }).then((resposta) => (resposta && resposta.data) || {})
  );


/** @deprecated use `classificarItens`. */
export const classificarItem = ({ empresaId, anoBase, aba, itemId, fase }) =>
  classificarItens({ empresaId, anoBase, aba, itens: [itemId], fase });

/** @deprecated use `classificarItens`. */
export const classificarLote = ({ empresaId, anoBase, aba, itens, fase }) =>
  classificarItens({ empresaId, anoBase, aba, itens, fase });

export default {
  listarItens,
  classificarItens,
  limparClassificacao,
  classificarItem,
  classificarLote,
  ABAS,
  FASES,
  labelFase,
  unidadeDaAba,
  formatarCompetencia,
};
